import { SVG, Box, Element as SvgElement } from '@svgdotjs/svg.js';

interface SVGImageInfo
{
  width: number;
  height: number;
  ratio: number;
  url: string;
}

export interface RegionLike
{
  x: number;
  y: number;
  width: number;
  height: number;
}

export class Point
{
  constructor(public x: number, public y: number)
  { }

  public add(other: Point): Point
  {
    return new Point(this.x + other.x, this.y + other.y);
  }
}

function clamp(x: number, low: number, high: number): number
{
  return (
      x < low
        ? low
        : x > high
            ? high
            : x
  );
}

function isBetween(x: number, low: number, high: number): boolean
{
  return x >= low && x <= high;
}

export class Region
{
  public static empty(): Region
  {
    return Region.fromSimilar({ x: 0, y: 0, width: 0, height: 0, });
  }

  public static fromSimilar(description: RegionLike): Region
  {
    return new Region(
        description.x, description.y, description.width, description.height);
  }

  public static fromTwoOrderedPoints(topLeft: Point, bottomRight: Point): Region
  {
    return new Region(
        topLeft.x,
        topLeft.y,
        bottomRight.x - topLeft.x,
        bottomRight.y - topLeft.y);
  }

  public static fromTwoPoints(pointA: Point, pointB: Point): Region
  {
    return Region.fromTwoOrderedPoints(
        new Point(
            Math.min(pointA.x, pointB.x),
            Math.min(pointA.y, pointB.y)),

        new Point(
            Math.max(pointA.x, pointB.x),
            Math.max(pointA.y, pointB.y)));
  }

  public static fromImageInfo(info: SVGImageInfo): Region
  {
    return new Region(0, 0, info.width, info.height);
  }

  public static fromBBox(bbox: Box): Region
  {
    return Region.fromSimilar(bbox);
  }

  public static fromRBox(rbox: Box): Region
  {
    return Region.fromSimilar(rbox);
  }

  constructor(
      public x: number,
      public y: number,
      public width: number,
      public height: number)
  {}

  public get topLeft(): Point
  {
    return new Point(this.x, this.y);
  }

  public get bottomRight(): Point
  {
    return new Point(this.x + this.width, this.y + this.height);
  }

  public clampPoint(point: Point): Point
  {
    return new Point(
        clamp(point.x, this.x, this.x + this.width),
        clamp(point.y, this.y, this.y + this.width));
  }

  public containsPoint(point: Point): boolean
  {
    return isBetween(point.x, this.x, this.x + this.width)
        && isBetween(point.y, this.y, this.y + this.height);
  }

  public clampRegion(region: Region): Region
  {
    // TODO ugly! failed cleanup attempts: |||

    const topLeft = region.topLeft;
    const bottomRight = region.bottomRight;
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    let x0 = topLeft.x;
    let x1 = bottomRight.x;
    let y0 = topLeft.y;
    let y1 = bottomRight.y;

    if (x0 < this.x) {
      x0 = this.x;
      x1 = x0 + width;
    }

    if (y0 < this.y) {
      y0 = this.y;
      y1 = y0 + height;
    }

    if (x1 > this.x + this.width) {
      x1 = this.x + this.width;
      x0 = x1 - width;
    }

    if (y1 > this.y + this.height) {
      y1 = this.y + this.height;
      y0 = y1 - height;
    }

    return Region.fromTwoOrderedPoints(
      new Point(x0, y0),
      new Point(x1, y1));
  }

  public clipRegion(region: Region): Region
  {
    if (region.x < this.x) {
      region.x = this.x;
    }
    if (region.y < this.y) {
      region.y = this.y;
    }
    if (region.height > this.height) {
      region.height = this.height;
    }
    if (region.width > this.width) {
      region.width = this.width;
    }

    return region;
  }
}

export function pinSVGElement(element: SvgElement, region: Region): void
{
  element
    .size(Math.round(region.width), Math.round(region.height))
    .move(Math.round(region.x), Math.round(region.y));
}
